import { defineField, defineType } from "sanity";

export const blogPost = defineType({
  name: "blogPost",
  title: "Blog Post",
  type: "document",
  fields: [
    defineField({
      name: "title",
      title: "Title",
      type: "string",
      description: "The headline for this post.",
      validation: (Rule) => Rule.required().error("A title is required.")
    }),
    defineField({
      name: "slug",
      title: "Slug",
      type: "slug",
      description: "The URL for this post (auto-generated from the title).",
      options: { source: "title", maxLength: 96 },
      validation: (Rule) => Rule.required().error("A slug is required.")
    }),
    defineField({
      name: "publishedAt",
      title: "Published at",
      type: "datetime",
      description: "Posts only appear on the site once this date has passed.",
      validation: (Rule) => Rule.required().error("A publish date is required.")
    }),
    defineField({
      name: "featuredImage",
      title: "Featured image",
      type: "image",
      description: "Hero image shown on the post and on the blog index card.",
      options: { hotspot: true },
      fields: [
        defineField({
          name: "alt",
          title: "Alt text",
          type: "string",
          description: "Describe the image for screen readers.",
          validation: (Rule) =>
            Rule.custom((value) => (value ? true : "Add alt text so the image is accessible.")).warning(
              "Add alt text so the image is accessible."
            )
        })
      ]
    }),
    defineField({
      name: "excerpt",
      title: "Excerpt",
      type: "text",
      rows: 3,
      description: "Short summary shown on the blog index."
    }),
    defineField({
      name: "body",
      title: "Body",
      type: "array",
      description: "The main content of the post.",
      of: [
        {
          type: "block",
          styles: [
            { title: "Normal", value: "normal" },
            { title: "Heading", value: "h2" },
            { title: "Subheading", value: "h3" },
            { title: "Quote", value: "blockquote" }
          ],
          lists: [
            { title: "Bullet", value: "bullet" },
            { title: "Numbered", value: "number" }
          ],
          marks: {
            decorators: [
              { title: "Bold", value: "strong" },
              { title: "Italic", value: "em" }
            ],
            annotations: [
              {
                name: "link",
                title: "Link",
                type: "object",
                fields: [
                  defineField({
                    name: "href",
                    title: "URL",
                    type: "url",
                    validation: (Rule) =>
                      Rule.uri({ scheme: ["http", "https", "mailto", "tel"] })
                  })
                ]
              }
            ]
          }
        },
        {
          type: "image",
          options: { hotspot: true },
          fields: [
            defineField({
              name: "alt",
              title: "Alt text",
              type: "string",
              description: "Describe the image for screen readers."
            })
          ]
        }
      ]
    })
  ],
  preview: {
    select: {
      title: "title",
      subtitle: "publishedAt",
      media: "featuredImage"
    },
    prepare: ({ title, subtitle, media }) => ({
      title: title || "Untitled post",
      subtitle: subtitle ? new Date(subtitle).toLocaleDateString() : "No publish date",
      media
    })
  }
});
